import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Sun, Moon, ListTodo, CheckCircle, Circle, Clock, MoreVertical } from 'lucide-react';
import { mockTasks, mockUsers } from '../../mock/data';

const ActionItemsList = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(true);
  const [filter, setFilter] = useState('all');

  const theme = {
    bg: darkMode ? 'bg-[#0a0a0f]' : 'bg-slate-50',
    card: darkMode ? 'bg-[#12121a]' : 'bg-white',
    cardHover: darkMode ? 'hover:bg-[#1a1a24]' : 'hover:bg-slate-50',
    text: darkMode ? 'text-white' : 'text-slate-900',
    textMuted: darkMode ? 'text-slate-400' : 'text-slate-500',
    border: darkMode ? 'border-white/5' : 'border-slate-200',
  };

  // Use real mock tasks
  const allTasks = mockTasks.map(task => ({
    ...task,
    assignee: mockUsers.find(u => u.id === task.assigneeId)?.name || 'Unassigned',
    list: ['Finance', 'CEO', 'Chairman', 'IT', 'HR'][Math.floor(Math.random() * 5)],
    urgent: task.priority === 'high',
  }));

  const filteredTasks = filter === 'all' 
    ? allTasks 
    : allTasks.filter(task => task.status === filter);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5 text-emerald-400" />;
      case 'in-progress': return <Clock className="w-5 h-5 text-amber-400" />;
      default: return <Circle className="w-5 h-5 text-slate-500" />;
    }
  };

  const getPriorityStyle = (priority) => {
    const styles = {
      'high': 'bg-red-500/10 text-red-400 border-red-500/20',
      'medium': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
      'low': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
    };
    return styles[priority] || styles.medium;
  };

  return (
    <div className={`min-h-screen ${theme.bg} ${theme.text}`}>
      {/* Ambient glow */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -left-40 w-80 h-80 bg-blue-500/10 rounded-full blur-[100px]" />
      </div>

      {/* Header */}
      <header className={`sticky top-0 z-40 ${theme.card}/80 backdrop-blur-xl border-b ${theme.border}`}>
        <div className="flex items-center justify-between px-4 py-3">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/mgmt-app')}
              className="p-2 rounded-xl hover:bg-white/5"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div className="flex items-center gap-2">
              <ListTodo className="w-5 h-5 text-blue-400" />
              <h1 className="font-semibold">Action Items</h1>
            </div>
          </div>
          <button 
            onClick={() => setDarkMode(!darkMode)}
            className="p-2 rounded-xl hover:bg-white/5"
          >
            {darkMode ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </button>
        </div>

        {/* Filters */}
        <div className="px-4 pb-3">
          <div className="flex gap-2 overflow-x-auto no-scrollbar">
            {['all', 'todo', 'in-progress', 'completed'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap transition-all ${
                  filter === f
                    ? 'bg-gradient-to-r from-blue-600 to-cyan-600 text-white'
                    : `${theme.card} ${theme.textMuted} border ${theme.border}`
                }`}
              >
                {f === 'todo' ? 'To Do' : f.charAt(0).toUpperCase() + f.slice(1).replace('-', ' ')}
              </button>
            ))}
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="relative z-10 p-4 space-y-2">
        {filteredTasks.map((task) => (
          <div 
            key={task.id} 
            className={`${theme.card} rounded-2xl border ${theme.border} p-4 ${theme.cardHover} transition-colors`}
          >
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                {getStatusIcon(task.status)}
              </div>
              <div className="flex-1 min-w-0">
                <p className={`font-medium ${task.status === 'completed' ? 'line-through opacity-50' : ''}`}>
                  {task.title}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full border ${getPriorityStyle(task.priority)}`}>
                    {task.priority}
                  </span>
                  {task.urgent && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                      Urgent
                    </span>
                  )}
                </div>
                <div className={`flex items-center gap-3 mt-3 text-xs ${theme.textMuted}`}>
                  <span>Due: {task.dueDate}</span>
                  <span>•</span>
                  <span>{task.assignee}</span>
                </div>
              </div>
              <button className="p-1 rounded-lg hover:bg-white/5">
                <MoreVertical className="w-4 h-4" />
              </button>
            </div>
          </div>
        ))}
      </main>
    </div>
  );
};

export default ActionItemsList;
